import { Dialog, DialogTitle } from "@gigpro/ui/Dialog";
import { Add, ArrowRight, Calendar, Send } from "@gigpro/ui/Icons";
import { toast } from "@gigpro/ui/Toast";
import { pluralize } from "@gigpro/utils";
import { useSet } from "@gigpro/utils/hooks/useSet";
import { ApiError } from "@sidegigllc/api";
import {
  getNextPageParam,
  getStafferGigsDirectOfferListInfiniteQueryKey,
  useStafferDirectOffersCreate,
  useStafferGigsDirectOfferListInfiniteWithTransform,
  type ProGroupListItem,
  type FindPro,
  type ShortGigsterPublicReadonlyProfile,
  type MergedGroupMember,
} from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { countSyncCreatedOffers, getDirectOfferError, showOfferSentToast } from "api/queries/gigDirectOffer";
import { useCreateWorkingInterview } from "api/queries/hire";
import { EmojiLoader } from "components/Loader/emoji";
import { useDirectOfferNoteGate } from "domains/admin/containers/DirectOfferNoteGate";
import { useIsAdmin } from "domains/auth";
import { BillingSetupRequiredDialog } from "domains/locations/containers/BillingSetupDialog";
import { OfferExistingGig } from "domains/pros/components/OfferExistingGig";
import React from "react";
import * as R from "remeda";
import { useActiveLocation } from "services/locationOrgContext";
import { captureException } from "utils/errorReporting";

type Variant = "default" | "candidate";
type OfferType = "select" | "existing";

type DialogCopy = {
  title: string;
  description: string;
};

type BaseProps = {
  offerType: OfferType | undefined;
  setOfferType(offerType: OfferType | undefined): void;
} & (
  | {
      pro: { user_id: number; display_name: string } & Partial<ShortGigsterPublicReadonlyProfile>;
      group?: never;
    }
  | {
      pro?: never;
      group: DirectOfferGroup;
    }
);

type DirectOfferGroup = ProGroupListItem & { members: Array<FindPro | MergedGroupMember> };
const resolveGroupIds = (group: DirectOfferGroup) =>
  "merged_group_ids" in group ? group.merged_group_ids : [group.id];

const handleDirectOfferError = (
  e: unknown,
  selectedProsCount: number,
  setIsBillingInfoModalOpen: (open: boolean) => void,
) => {
  const apiErrors = ApiError.fromThrown(e);
  const errorMsg = apiErrors.getMainErrorMessage();

  if (errorMsg === "You need to complete payment information for this location") {
    setIsBillingInfoModalOpen(true);
    return;
  }

  // If there is only one pro selected, show the specific error message from the API
  const detailedErrorMsg = selectedProsCount === 1 ? apiErrors.getDetails()?.[0]?.message || errorMsg : errorMsg;
  toast.error(detailedErrorMsg);
};

const showOfferResultToast = (res: Parameters<typeof getDirectOfferError>[0], selectedProsCount: number) => {
  if ("advisory_errors" in res) {
    showOfferSentToast(selectedProsCount, res.queued_count, res.advisory_errors.length);
    return;
  }
  const partialError = getDirectOfferError(res);
  if (partialError) toast.warning(partialError.message);
  else showOfferSentToast(selectedProsCount, countSyncCreatedOffers(res, selectedProsCount), 0);
};

const maybeCreateWorkingInterview = async (
  gigId: number,
  variant: Variant,
  candidateId: string | undefined,
  jobPostingId: string | undefined,
  createWorkingInterview: (data: { gigs: Array<number> }) => Promise<unknown>,
) => {
  if (variant === "candidate" && candidateId && jobPostingId) {
    try {
      await createWorkingInterview({ gigs: [gigId] });
    } catch (error) {
      captureException(error);
    }
  }
};

type SendDirectOfferContainerProps = BaseProps &
  (
    | { variant?: "default"; candidateId?: never; jobPostingId?: never }
    | { variant: "candidate"; candidateId: string; jobPostingId: string }
  );

const variantCopy: Record<Variant, (targetLabel: string) => DialogCopy> = {
  default: (targetLabel) => ({
    title: "Send direct offer",
    description: `Offer a new or existing shift to this ${targetLabel}`,
  }),
  candidate: () => ({
    title: "Offer shift",
    description:
      "Send shift offers to candidates to work a paid shift, experience their skills first hand, and hire with confidence.",
  }),
};

export const SendDirectOfferContainer: React.FC<SendDirectOfferContainerProps> = ({
  offerType,
  setOfferType,
  pro,
  group,
  variant = "default",
  candidateId,
  jobPostingId,
}) => {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const isSchedule = !!matchRoute({ to: "/locations/$locationId/schedule" });

  const isGroup = !pro && !!group;
  const targetLabel = isGroup ? "group" : "Pro";
  const copy = variantCopy[variant](targetLabel);

  const [activeLocation] = useActiveLocation();

  // Forward a nullable multiplier instead of the raw toggle so the picker stays presentational.
  const hdSurchargeMultiplier = activeLocation?.allow_high_demand_pros
    ? activeLocation.high_demand_surcharge_multiplier
    : undefined;

  const queryClient = useQueryClient();
  const {
    data,
    isLoading: gigsLoading,
    hasNextPage,
    fetchNextPage,
  } = useStafferGigsDirectOfferListInfiniteWithTransform(
    {
      page_size: 50,
      pro_id: pro?.user_id ?? group?.member_previews?.[0]?.user_id ?? -1,
      location_name: activeLocation?.name,
    },
    {
      query: {
        enabled: !!offerType,
        getNextPageParam,
        select: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({ ...page, results: page.results })),
        }),
      },
    },
  );

  const [gigsOffered, gigsOfferedActions] = useSet<number>();
  const gigs = data?.pages.flatMap((page) => page.results) ?? [];
  const gigsBySkill =
    gigs.length === 0
      ? []
      : R.pipe(
          gigs,
          R.map((gig) =>
            isGroup
              ? {
                  ...gig,
                  is_selected: gigsOffered.has(gig.id),
                  is_confirmed: false,
                  is_filled: false,
                  is_cancelled: false,
                }
              : gig,
          ),
          R.groupBy((gig) => gig.skill),
          R.entries(),
        );

  const shouldFetchNextPage = hasNextPage && gigs.length < 300;
  React.useEffect(() => {
    if (shouldFetchNextPage) fetchNextPage();
  }, [shouldFetchNextPage, fetchNextPage]);

  const [isBillingInfoModalOpen, setIsBillingInfoModalOpen] = React.useState(false);

  const { mutateAsync: sendOffer } = useStafferDirectOffersCreate({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getStafferGigsDirectOfferListInfiniteQueryKey() }),
    },
  });

  const { mutateAsync: createWorkingInterview } = useCreateWorkingInterview(jobPostingId ?? "", candidateId);
  const isAdmin = useIsAdmin();
  const noteGate = useDirectOfferNoteGate();

  if (!activeLocation) return null;

  const onOfferGig = async (gigId: number) => {
    const selectedProsCount = pro ? 1 : group.members_count;
    const recipients = pro ? "this pro" : `${selectedProsCount} ${pluralize(selectedProsCount, "pro")}`;
    const note = isAdmin ? await noteGate.requestNote(recipients, selectedProsCount) : null;
    if (isAdmin && !note) return;

    try {
      const res = await sendOffer({
        data: {
          application_type: "DIRECT_OFFER",
          gig_id: gigId,
          is_recurring: false,
          pro_ids: pro ? [pro.user_id] : undefined,
          group_ids: group ? resolveGroupIds(group) : undefined,
          ...(note && { ...note, surface: "OTHER" }),
        },
        params: pro ? { is_async: true } : undefined,
      });

      showOfferResultToast(res, selectedProsCount);

      gigsOfferedActions.add(gigId);

      await maybeCreateWorkingInterview(gigId, variant, candidateId, jobPostingId, createWorkingInterview);
    } catch (e) {
      handleDirectOfferError(e, selectedProsCount, setIsBillingInfoModalOpen);
    }
  };
  const onClose = () => setOfferType(undefined);
  const onCreateShift = (postType: "single" | "schedule") => {
    setOfferType(undefined);
    const params = {
      post_gig_type: postType,
      offer_pro: pro?.user_id,
      offer_group: group ? resolveGroupIds(group).join(",") : undefined,
      return_to: location.pathname + location.search,
      // Pass candidate and job IDs for working interview context
      offer_candidate_id: variant === "candidate" ? candidateId : undefined,
      offer_job_posting_id: variant === "candidate" ? jobPostingId : undefined,
    } as const;
    if (isSchedule) {
      void navigate({ to: ".", search: (prev) => ({ ...prev, ...params }), replace: true });
      return;
    }
    void navigate({
      to: "/locations/$locationId/schedule",
      params: { locationId: String(activeLocation.id) },
      search: params,
    });
  };

  return (
    <>
      <Dialog open={offerType != null} onClose={onClose} closeOnBackdropClick>
        <DialogTitle description={copy.description}>{copy.title}</DialogTitle>
        {gigsBySkill == null ? (
          <EmojiLoader />
        ) : (
          <>
            {offerType === "existing" &&
              (gigsLoading ? (
                <EmojiLoader />
              ) : (
                <OfferExistingGig
                  gigsBySkill={gigsBySkill}
                  onOfferGig={onOfferGig}
                  proName={pro?.display_name}
                  surchargeMultiplier={hdSurchargeMultiplier}
                  group={isGroup ? { name: group.name, membersCount: group.members_count } : undefined}
                />
              ))}
            {offerType === "select" && (
              <div className="flex flex-col gap-2 text-normal">
                <button
                  type="button"
                  className="flex gap-2 rounded-sm bg-background p-2 font-semibold text-brand disabled:text-gray"
                  onClick={() => setOfferType("existing")}
                  data-testid="btn_offer_existing_shift"
                >
                  <Send className="size-4" />
                  Offer an existing shift
                  <ArrowRight className="ml-auto size-4" />
                </button>
                <button
                  type="button"
                  className="flex gap-2 rounded-sm bg-background p-2 font-semibold text-brand disabled:text-gray"
                  onClick={() => onCreateShift("single")}
                  data-testid="btn_create_shift"
                >
                  <Add className="size-4" />
                  Create a shift for this {targetLabel}
                  <ArrowRight className="ml-auto size-4" />
                </button>
                <button
                  type="button"
                  className="flex gap-2 rounded-sm bg-background p-2 font-semibold text-brand disabled:text-gray"
                  onClick={() => onCreateShift("schedule")}
                  data-testid="btn_create_schedule"
                >
                  <Calendar className="size-4" />
                  Create a schedule for this {targetLabel}
                  <ArrowRight className="ml-auto size-4" />
                </button>
              </div>
            )}
          </>
        )}
      </Dialog>
      {noteGate.dialog}
      <BillingSetupRequiredDialog open={isBillingInfoModalOpen} onClose={() => setIsBillingInfoModalOpen(false)} />
    </>
  );
};
